/**
 * BiometricStore — 2nd factor opcional para unlock do vault (HLOS-45).
 *
 * AC3-S4 da REQ-006: "Biometria opcional como 2º fator de desbloqueio".
 * Biometria NUNCA substitui passphrase (CL-08). É camada de conveniência
 * acima do vault (HLOS-21 plugin).
 *
 * Constitucional:
 * - CL-07 (Modelo = ativo): biometria roda em Secure Enclave/Keystore
 * - CL-08 (Ética fechada): token biométrico cifrado em OPFS, descriptografável
 *   apenas com master key in-memory; zero plaintext exposto à plataforma
 * - CC-05 (zero penalidade): 3+ falhas biométricas → fallback automático
 *   para passphrase, sem lockout
 * - Princípio X (Dignidade): sem fricção viciante, opt-in total
 *
 * Em v0.3 (dev), o serviço real (`@aparajita/capacitor-biometric-auth`)
 * só funciona no Capacitor runtime. Em testes, expomos interface estável
 * para mock. HLOS-44.5+ integra com vault.
 */

import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { Preferences } from "@capacitor/preferences";
import { isBiometricAvailable } from "./service";
import { BiometricKeyManager, wipeLegacyBiometricDataFromPreferences } from "./keyManager";

/**
 * HLOS-175 + HLOS-176 + HLOS-176v2: persistir flag `hasBlob` em DOIS storages
 * para máxima robustez no iOS:
 *
 * 1. `@capacitor/preferences` → UserDefaults (iOS) — persistente nativo
 * 2. `localStorage` → WebView storage — fallback sync
 *
 * Por quê dupla persistência: detectamos que no iOS WKWebView, mesmo com
 * o plugin nativo Preferences, o flag pode ser perdido em alguns cenários:
 * - iOS 17+ "purge unused WebView data"
 * - WebView recreation após force quit (alguns apps)
 * - Plugin Preferences fallback para web (caso bridge nativo não detecte)
 *
 * Por que precisamos: `hasBlob` em Pinia state é volátil (perde entre sessões).
 * Vault locked → `loadBlob()` retorna null (não consegue ler blob cifrado).
 *
 * LGPD: Preferences/localStorage armazenam **apenas "1" ou "0"** — zero PII.
 *
 * Constitucional:
 * - CL-07 (vault soberano): blob cifrado continua no vault; flag é só UX hint.
 * - CL-08 (Ética fechada): zero info leakage (apenas boolean).
 */
const HAS_BLOB_PREF_KEY = "hlos.biometric.hasBlob";
const HAS_BLOB_LS_KEY = "hlos.biometric.hasBlob";

/** HLOS-176v2: pedir storage persistente para reduzir chance de purge iOS. */
async function requestPersistentStorage(): Promise<void> {
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.storage &&
      typeof navigator.storage.persist === "function"
    ) {
      const alreadyPersisted = await navigator.storage.persisted?.();
      if (!alreadyPersisted) {
        await navigator.storage.persist();
      }
    }
  } catch {
    // Silent fail — storage persistente é best-effort.
  }
}

async function persistHasBlob(value: boolean): Promise<void> {
  const v = value ? "1" : "0";
  // Pedir persistent storage antes de salvar (best-effort).
  await requestPersistentStorage();

  // Tentar Preferences (UserDefaults iOS) primeiro — fonte primária.
  try {
    await Preferences.set({ key: HAS_BLOB_PREF_KEY, value: v });
  } catch (e) {
    console.warn("[biometric] Preferences.set failed:", e);
  }

  // Fallback: localStorage (sync, redundante).
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(HAS_BLOB_LS_KEY, v);
    }
  } catch (e) {
    console.warn("[biometric] localStorage.setItem failed:", e);
  }
}

async function readHasBlobFromStorage(): Promise<boolean> {
  // Tentar Preferences primeiro.
  try {
    const { value } = await Preferences.get({ key: HAS_BLOB_PREF_KEY });
    if (value === "1") return true;
  } catch (e) {
    console.warn("[biometric] Preferences.get failed:", e);
  }

  // Fallback: localStorage.
  try {
    if (typeof localStorage !== "undefined") {
      if (localStorage.getItem(HAS_BLOB_LS_KEY) === "1") return true;
    }
  } catch (e) {
    console.warn("[biometric] localStorage.getItem failed:", e);
  }

  return false;
}

export type BiometricStatus =
  | "unknown"      // ainda não checou disponibilidade
  | "available"    // hardware + permission OK
  | "unavailable"  // sem hardware ou sem permission
  | "denied";      // usuário negou permission

export const useBiometricStore = defineStore("biometric", () => {
  const enabled = ref(false);
  const status = ref<BiometricStatus>("unknown");
  const lastError = ref<string | null>(null);
  const consecutiveFailures = ref(0);
  /**
   * HLOS-173: blob biométrico cifrado existe em OPFS?
   * HLOS-175: flag persistido em Preferences (UserDefaults iOS).
   * HLOS-176: inicializa como false; bootHasBlob() lê de Preferences no boot.
   */
  const hasBlob = ref(false);

  /** Marca biometria como habilitada (após setup bem-sucedido). */
  function enable(): void {
    enabled.value = true;
    consecutiveFailures.value = 0;
    lastError.value = null;
  }

  /** Desabilita biometria; usuário volta a usar passphrase-only. */
  function disable(): void {
    enabled.value = false;
    consecutiveFailures.value = 0;
    lastError.value = null;
  }

  /** Marca biometria disponível no device. */
  function setAvailable(): void {
    status.value = "available";
  }

  /** Marca biometria indisponível (sem hardware / sem permission). */
  function setUnavailable(): void {
    status.value = "unavailable";
  }

  /** Marca biometria negada pelo usuário. */
  function setDenied(): void {
    status.value = "denied";
  }

  /**
   * Registra falha na autenticação biométrica.
   * CC-05: 3+ falhas → habilita fallback automático para passphrase.
   */
  function recordFailure(reason: string): void {
    consecutiveFailures.value += 1;
    lastError.value = reason;
    if (consecutiveFailures.value >= 3) {
      // Sugere fallback — caller decide.
      console.info("[biometric] 3+ falhas; sugerir fallback para passphrase");
    }
  }

  function clearError(): void {
    lastError.value = null;
  }

  function reset(): void {
    enabled.value = false;
    status.value = "unknown";
    lastError.value = null;
    consecutiveFailures.value = 0;
    hasBlob.value = false;
  }

  /**
   * HLOS-173: detecta disponibilidade de biometria no device.
   * Chama `@aparajita/capacitor-biometric-auth` via `service.ts`.
   * Atualiza `status` para `available` ou `unavailable`.
   * Fail-safe: qualquer erro → `unavailable` (nunca lançar).
   */
  async function initFromDevice(): Promise<void> {
    try {
      const avail = await isBiometricAvailable();
      if (avail.available) {
        setAvailable();
      } else {
        setUnavailable();
      }
    } catch (e) {
      // Em web/Vite dev sem Capacitor, o plugin lança — não é erro fatal.
      console.info("[biometric] initFromDevice failed (provavelmente web/dev):", e);
      setUnavailable();
    }
  }

  /**
   * HLOS-173: lê do vault se já existe blob biométrico cifrado.
   * HLOS-175/176: sincroniza com Preferences (caso flag e vault divergem).
   */
  async function loadBlobStatus(): Promise<void> {
    try {
      const present = await BiometricKeyManager.hasBiometricEnabled();
      // Se vault unlocked revela que blob NÃO existe (foi limpo
      // por outro caminho), atualizar flag Preferences também.
      if (!present && hasBlob.value) {
        await persistHasBlob(false);
      }
      hasBlob.value = present;
      if (present) {
        enabled.value = true;
      }
    } catch (e) {
      console.warn("[biometric] loadBlobStatus failed:", e);
      // Mantém hasBlob do Preferences (vault locked é o caso comum).
    }
  }

  /**
   * HLOS-175/176: action para ser chamada 1x no boot do app (App.vue).
   * Carrega `hasBlob` de Preferences (UserDefaults iOS — persistente).
   *
   * HLOS-176: também limpa dados legacy de HLOS-176v3 WIP (Preferences
   * plaintext) antes de ler flag. Se wipe removeu algo, reset `hasBlob`
   * flag também — coerência de estado (sem blob real, flag deve ser false
   * para forçar re-configuração via BiometricToggle na Home).
   *
   * Idempotente. Async porque Preferences.get() é async.
   */
  async function bootHasBlob(): Promise<void> {
    // HLOS-176 Opção A: wipe legacy data de HLOS-176v3 WIP (Preferences plaintext).
    // Se wipe removeu algo, reset flag para forçar re-config (coerência).
    const wiped = await wipeLegacyBiometricDataFromPreferences();
    hasBlob.value = await readHasBlobFromStorage();
    if (wiped && hasBlob.value) {
      await persistHasBlob(false);
      hasBlob.value = false;
      console.info("[HLOS-176] legacy data wiped; hasBlob reset to false (re-configure via BiometricToggle)");
    }
  }

  /**
   * HLOS-173: setup biométrico real (chave master cifrada com biometric-bound key).
   * Requer master key de 32 bytes do vault unlocked.
   * Aciona prompt Face ID / Touch ID / Fingerprint (CL-08: vault soberano).
   * Em caso de sucesso: `hasBlob=true`, `enabled=true`.
   */
  async function setupBiometric(masterKey: Uint8Array): Promise<void> {
    if (masterKey.byteLength !== 32) {
      throw new Error(
        `[biometric] masterKey deve ter 32 bytes, recebido: ${masterKey.byteLength}`,
      );
    }
    await BiometricKeyManager.setupBiometricUnlock(masterKey, {
      reason: "Habilitar desbloqueio biométrico",
      cancelTitle: "Cancelar",
      iosFallbackTitle: "Usar passphrase",
    });
    // HLOS-176: persistir flag em Preferences (UserDefaults iOS — persistente).
    await persistHasBlob(true);
    hasBlob.value = true;
    enable();
  }

  /**
   * HLOS-173: desabilita biometria (remove blob cifrado do vault).
   * CC-05: zero penalidade; pode re-habilitar depois.
   * HLOS-176: também limpa flag em Preferences.
   */
  async function disableBiometric(): Promise<void> {
    try {
      await BiometricKeyManager.disableBiometricUnlock();
    } finally {
      await persistHasBlob(false);
      hasBlob.value = false;
      disable();
    }
  }

  /**
   * HLOS-173: pode habilitar biometria agora?
   * Combina `status === "available"` + `!enabled` (opt-in).
   */
  const canEnableBiometric = computed(
    () => status.value === "available" && !enabled.value,
  );

  return {
    enabled,
    status,
    lastError,
    consecutiveFailures,
    hasBlob,
    canEnableBiometric,
    enable,
    disable,
    setAvailable,
    setUnavailable,
    setDenied,
    recordFailure,
    clearError,
    reset,
    initFromDevice,
    loadBlobStatus,
    setupBiometric,
    disableBiometric,
    bootHasBlob,
  };
});
